const MY_SHOPIFY_HOST_SUFFIX = '.myshopify.com';

/**
 * Canonical hostname (lowercase, no trailing dot, no www) for comparing Shopify store URLs
 * (e.g. delete confirmation). Accepts full URLs or bare hostnames.
 */
export function normalizeShopifyDomainForMatch(raw: string): string {
  const t = String(raw ?? '').trim();
  if (!t) return '';
  const lower = t.toLowerCase();
  const href = /^https?:\/\//i.test(lower) ? lower : `https://${lower}`;
  try {
    const u = new URL(href);
    return u.hostname.replace(/\.$/, '').replace(/^www\./, '');
  } catch {
    return lower
      .replace(/^https?:\/\//, '')
      .split('/')[0]
      ?.replace(/\.$/, '') ?? '';
  }
}

/** Strips spaces, collapses underscores to hyphens, keeps only hostname-safe characters for *.myshopify.com. */
function normalizeShopifyHandleSegment(handle: string): string {
  let h = handle.trim().toLowerCase();
  while (h.endsWith('.')) {
    h = h.slice(0, -1);
  }
  h = h.replace(/\s+/g, '').replace(/_+/g, '-');
  h = h.replace(/[^a-z0-9-]/g, '');
  h = h.replace(/-+/g, '-').replace(/^-+|-+$/g, '');
  return h;
}

/**
 * Normalizes Shopify onboarding input into an admin subdomain handle and full *.myshopify.com host for display.
 * Strips trailing dots, accepts plain handles, full myshopify.com hosts, and common URL shapes.
 * Underscores in the handle are normalized to hyphens for the Shopify hostname (spaces are not allowed).
 */
export function parseShopifyShopInput(raw: string): { handle: string; fullDomain: string } | null {
  let s = (raw ?? '').trim().toLowerCase();
  while (s.endsWith('.')) {
    s = s.slice(0, -1);
  }
  if (!s) return null;

  let hostOrHandle = s;

  if (/^https?:\/\//i.test(s)) {
    try {
      hostOrHandle = new URL(s).hostname;
    } catch {
      return null;
    }
  } else if (s.includes('/')) {
    try {
      hostOrHandle = new URL(`https://${s}`).hostname;
    } catch {
      hostOrHandle = (s.split('/')[0] ?? '').trim();
    }
  }

  while (hostOrHandle.endsWith('.')) {
    hostOrHandle = hostOrHandle.slice(0, -1);
  }
  if (!hostOrHandle) return null;

  let handle: string;
  if (hostOrHandle.endsWith(MY_SHOPIFY_HOST_SUFFIX)) {
    handle = hostOrHandle.slice(0, -MY_SHOPIFY_HOST_SUFFIX.length);
  } else if (!hostOrHandle.includes('.')) {
    handle = hostOrHandle;
  } else {
    return null;
  }

  handle = normalizeShopifyHandleSegment(handle);
  if (!handle) return null;

  return { handle, fullDomain: `${handle}.myshopify.com` };
}
